import { Link } from 'react-router-dom'

import { CabecalhoPagina, Mensagem, Tag } from '../../components/ui'
import { useAuth } from '../auth/store'

/**
 * Painel inicial.
 *
 * O conteudo muda por PERMISSAO, nao por papel: assim um papel customizado
 * criado na tela de admin ja aparece corretamente aqui, sem alteracao de codigo.
 */

interface Atalho {
  para: string
  titulo: string
  descricao: string
  icone: string
  permissao?: string
}

const ATALHOS: Atalho[] = [
  {
    para: '/fretes',
    titulo: 'Buscar fretes',
    descricao: 'Vitrine de fretes planejados, com filtro por rota, janela e remuneração.',
    icone: 'fa-search',
    permissao: 'freight:read:public',
  },
  {
    para: '/fretes/novo',
    titulo: 'Publicar frete',
    descricao: 'Carga, paradas, janela de coleta e entrega, remuneração.',
    icone: 'fa-plus',
    permissao: 'freight:create',
  },
  {
    para: '/motorista/cadastro',
    titulo: 'Meu cadastro',
    descricao: 'CNH, veículo e base operacional.',
    icone: 'fa-id-card',
    permissao: 'driver:update:own',
  },
  {
    para: '/motorista/agenda',
    titulo: 'Minha agenda',
    descricao: 'Quando e por onde você vai rodar. Alimenta a compatibilidade.',
    icone: 'fa-calendar-week',
    permissao: 'driver:update:own',
  },
  {
    para: '/operacao/verificacoes',
    titulo: 'Fila de verificação',
    descricao: 'Aprovar ou rejeitar documentos de motoristas e veículos.',
    icone: 'fa-clipboard-check',
    permissao: 'driver:verify',
  },
  {
    para: '/admin/usuarios',
    titulo: 'Usuários',
    descricao: 'Buscar, suspender e reativar contas.',
    icone: 'fa-users',
    permissao: 'user:read',
  },
  {
    para: '/admin/papeis',
    titulo: 'Papéis e permissões',
    descricao: 'Ver a matriz de acesso em vigor.',
    icone: 'fa-shield-alt',
    permissao: 'role:read',
  },
  {
    para: '/admin/auditoria',
    titulo: 'Trilha de auditoria',
    descricao: 'Tudo que foi feito na plataforma, por quem e quando.',
    icone: 'fa-clipboard-list',
    permissao: 'admin:audit:read',
  },
]

export function Painel() {
  const { usuario, pode } = useAuth()
  if (!usuario) return null

  const atalhos = ATALHOS.filter((a) => !a.permissao || pode(a.permissao))
  const primeiroNome = usuario.name.split(' ')[0] ?? usuario.name
  const ePrivilegiado =
    usuario.roles.includes('ADMIN_MASTER') || usuario.roles.includes('OPERATOR')

  return (
    <div className="ifr-pagina">
      <CabecalhoPagina
        titulo={`Olá, ${primeiroNome}`}
        apoio={
          usuario.roles.includes('DRIVER')
            ? 'Monte sua agenda escolhendo fretes que encaixam na sua rota.'
            : usuario.roles.includes('SHIPPER')
              ? 'Publique cargas com janela definida e receba candidaturas de motoristas verificados.'
              : 'Painel da plataforma iFretes.'
        }
      />

      <div className="ifr-pilha">
        {usuario.mustChangePassword && (
          <Mensagem tipo="warning" titulo="Troque sua senha">
            Esta conta usa a senha inicial gerada na instalação.{' '}
            <Link to="/minha-conta">Defina uma senha própria</Link> antes de continuar.
          </Mensagem>
        )}

        {!usuario.mfaEnabled && ePrivilegiado && (
          <Mensagem tipo="danger" titulo="Segundo fator obrigatório pendente">
            Seu papel exige verificação em dois fatores.{' '}
            <Link to="/minha-conta/mfa">Ativar agora</Link>.
          </Mensagem>
        )}

        {!usuario.mfaEnabled && !ePrivilegiado && (
          <Mensagem tipo="info" titulo="Proteja sua conta">
            A verificação em dois fatores é opcional para você, mas recomendada.{' '}
            <Link to="/minha-conta/mfa">Ativar</Link>.
          </Mensagem>
        )}
      </div>

      <section className="ifr-pilha ifr-pilha--curta">
        <h2 className="ifr-titulo ifr-titulo--secao">O que você pode fazer</h2>
        <div className="ifr-grade">
          {atalhos.map((a) => (
            <Link key={a.para} to={a.para} className="ifr-cartao ifr-atalho">
              <span aria-hidden="true" className="ifr-icone-bloco">
                <i className={`fas ${a.icone}`} />
              </span>
              <h3 className="ifr-atalho__titulo">{a.titulo}</h3>
              <p className="ifr-nota ifr-fraco">{a.descricao}</p>
            </Link>
          ))}
        </div>
      </section>

      <details className="ifr-detalhes">
        <summary className="ifr-detalhes__resumo">
          Suas permissões nesta conta ({usuario.permissions.length})
        </summary>
        <div className="ifr-tags ifr-secao__corpo">
          {usuario.permissions.map((p) => (
            <span key={p} className="ifr-codigo">
              {p}
            </span>
          ))}
        </div>
      </details>

      <div className="ifr-tags">
        {usuario.roles.map((r) => (
          <Tag key={r} variante="marca" icone="fa-user-shield">
            {r}
          </Tag>
        ))}
      </div>
    </div>
  )
}
